import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import LuminateArtistDiscoveryFilters, {
  DEFAULT_LUMINATE_ARTIST_DISCOVERY_FILTERS,
} from '../components/snoop/LuminateArtistDiscoveryFilters'
import { fetchLuminateArtistsSummary, fetchLuminateArtistHistory, updateLuminateStatus } from '../api/luminateClient'
import {
  LUMINATE_EXCLUDED_STATUSES,
  LUMINATE_STATUS_OPTIONS,
  luminateRowsToRemovePredicate,
  luminateStatusPolarity,
} from '../utils/snoop/statusOptions'
import { PLACEHOLDER_LUMINATE_ARTISTS } from '../utils/snoop/placeholderColumns'

const ARTIST_TRUNCATE = [
  'Name',
  'Luminate Artist ID',
  'Imprint',
  'Luminate Genres',
  'Country of Origin',
  'Language',
]
const ARTIST_NEC_COLUMNS = ['Name']
const ARTIST_HIDDEN_COLUMNS = ['Focus Country-Lang', 'Imprint DK/Self']

const LUMINATE_ARTIST_OUTREACH_CONFIG = {
  artistColumn: 'Name',
  songTitleColumns: ['Biggest Track Title', 'Latest Track Title'],
  confirmMultiArtistName: true,
}

/** Artist / label tiers only (no track). */
const ARTIST_STATUS_OPTIONS = LUMINATE_STATUS_OPTIONS.filter((opt) => {
  const v = typeof opt === 'string' ? opt : opt.value
  return /Artist|Label/i.test(String(v))
})

export default function LuminateArtistsSummaryPage() {
  const [discoveryFilters, setDiscoveryFilters] = useState(
    DEFAULT_LUMINATE_ARTIST_DISCOVERY_FILTERS,
  )
  const [hideNonEnglish, setHideNonEnglish] = useState(true)
  const fetchData = useCallback(
    () => fetchLuminateArtistsSummary(discoveryFilters),
    [discoveryFilters],
  )

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'luminate',
        uidColumn: 'Luminate Artist ID',
        options: ARTIST_STATUS_OPTIONS,
        removeAfterSave: true,
        luminateCascadeColumns: {
          artistCol: 'Name',
          imprintCol: 'Imprint',
          songIdCol: null,
        },
        shouldRemoveRow: (status) => {
          const s = String(status || '')
          return LUMINATE_EXCLUDED_STATUSES.has(s) || luminateStatusPolarity(s) === 'Signed'
        },
        rowsToRemove: (status, ctx) =>
          luminateRowsToRemovePredicate(status, ctx, {
            artistCol: 'Name',
            imprintCol: 'Imprint',
            songIdCol: null,
          }),
        onSave: async ({ status, row }) => {
          await updateLuminateStatus({
            status,
            primaryArtist: row?.Name,
            displayArtist: row?.Name,
            imprint: row?.Imprint,
            artistLuminateId: row?.['Luminate Artist ID'],
          })
        },
      },
    }),
    [],
  )

  return (
    <SummaryDashboard
      title="Luminate Artists"
      subtitle="Artist multiweek · streams with WoW % · score"
      fetchData={fetchData}
      defaultSort={[
        { column: 'Score', direction: 'desc' },
      ]}
      searchPlaceholder="Search artist, status, imprint…"
      layout="unsigned"
      truncateColumns={ARTIST_TRUNCATE}
      hiddenColumns={ARTIST_HIDDEN_COLUMNS}
      placeholderColumns={PLACEHOLDER_LUMINATE_ARTISTS}
      editableStatus={editableStatus}
      enableOutreachColumn
      outreachConfig={LUMINATE_ARTIST_OUTREACH_CONFIG}
      linkLabel="ID"
      enableHideNonEnglish
      hideNonEnglish={hideNonEnglish}
      onHideNonEnglishChange={setHideNonEnglish}
      showHideNonEnglishToggle={false}
      hideNonEnglishColumns={ARTIST_NEC_COLUMNS}
      enableGenreStartsWithFilter
      enableCountryFilter
      countryColumn="Country of Origin"
      expandableHistory
      stackPctChgUnderStreams
      historyFetcher={async (row) => {
        const artistId = row?.['Luminate Artist ID']
        if (!artistId) return { series: [] }
        return fetchLuminateArtistHistory(artistId)
      }}
      extraActions={
        <LuminateArtistDiscoveryFilters
          filters={discoveryFilters}
          onChange={setDiscoveryFilters}
          hideNonEnglish={hideNonEnglish}
          onHideNonEnglishChange={setHideNonEnglish}
        />
      }
    />
  )
}
